import {
  CopyOutlined, DeleteOutlined, FileTextOutlined, MessageOutlined,
  MinusOutlined, MoreOutlined, SendOutlined, StopOutlined
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { Button, Dropdown, Input, Skeleton, Tooltip } from 'antd'
import type { KeyboardEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DESKTOP_MEDIA_QUERY } from './ContextChatPanel.props'
import type { ContextChatMessage, ContextChatPanelProps } from './ContextChatPanel.props'
import * as S from './style'

export function ContextChatPanel<Message extends ContextChatMessage> ({
  title, dialogLabel, launcherLabel, emptyTitle, placeholder, suggestions,
  messages, draft, isSending, isLoadingHistory = false, isResetting = false,
  autoOpenStorageKey, notice, onDraftChange, onSend, onStop, onCopy,
  onReset, renderMessage, composerControls, isInputDisabled = false, onMinimize, embedded = false,
  avatarSrc, headerActions, showComposerContext = true
}: ContextChatPanelProps<Message>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const isVisible = embedded || isOpen
  const panelRef = useRef<HTMLElement | null>(null)
  const messagesAreaRef = useRef<HTMLDivElement | null>(null)
  const launcherRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<React.ElementRef<typeof Input.TextArea> | null>(null)
  const lastMessageKeyRef = useRef('')

  const minimize = useCallback(() => {
    onMinimize?.()
    setIsOpen(false)
    window.requestAnimationFrame(() => launcherRef.current?.focus())
  }, [onMinimize])

  const inputKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
    event.preventDefault()
    onSend()
  }, [onSend])

  const controlsDisabled = isLoadingHistory || isSending || isResetting || isInputDisabled
  const menuItems: MenuProps['items'] = [
    {
      key: 'copy',
      icon: <CopyOutlined aria-hidden="true" />,
      label: 'Copiar conversa',
      disabled: !messages.length || controlsDisabled
    },
    ...(!embedded
      ? [{ type: 'divider' as const },
          {
            key: 'reset',
            icon: <DeleteOutlined aria-hidden="true" />,
            label: 'Resetar chat',
            danger: true,
            disabled: !messages.length || controlsDisabled
          }]
      : [])
  ]

  useEffect(() => {
    if (!autoOpenStorageKey || !window.matchMedia(DESKTOP_MEDIA_QUERY).matches) return
    try {
      if (window.localStorage.getItem(autoOpenStorageKey)) return
      window.localStorage.setItem(autoOpenStorageKey, 'true')
    } catch {
      setIsOpen(true)
      return
    }
    setIsOpen(true)
  }, [autoOpenStorageKey])

  useEffect(() => {
    if (!isVisible) return
    const lastMessage = messages[messages.length - 1]
    const messageKey = lastMessage ? `${lastMessage.id}:${lastMessage.content}` : ''
    if (embedded && lastMessageKeyRef.current === messageKey) return
    lastMessageKeyRef.current = messageKey
    const frame = window.requestAnimationFrame(() => {
      const area = messagesAreaRef.current
      if (area) area.scrollTop = area.scrollHeight
    })
    return () => window.cancelAnimationFrame(frame)
  }, [isVisible, messages, embedded])

  useEffect(() => {
    if (!isVisible) return
    if (window.matchMedia(DESKTOP_MEDIA_QUERY).matches) inputRef.current?.focus({ preventScroll: true })
    else panelRef.current?.focus({ preventScroll: true })
  }, [isVisible])

  const content = (
    <S.ChatRoot $embedded={embedded}>
      {isVisible
        ? <S.ChatPanel
            $embedded={embedded} ref={panelRef} tabIndex={-1}
            role={embedded ? 'region' : 'dialog'} aria-label={dialogLabel}
            onKeyDown={event => { if (!embedded && event.key === 'Escape') minimize() }}
          >
          <S.ChatHeader $embedded={embedded}>
            <S.ChatHeading>
              {avatarSrc && <S.ChatAvatar src={avatarSrc} alt='' aria-hidden='true' width={36} height={36} />}
              <Tooltip title={`Nora • ${title}`}>
                <S.ChatTitle $embedded={embedded}><strong>Nora</strong><span>{title}</span></S.ChatTitle>
              </Tooltip>
            </S.ChatHeading>
            <S.HeaderActions>
              {!embedded && <S.BetaBadge>Beta</S.BetaBadge>}
              <Dropdown
                menu={{
                  items: menuItems,
                  onClick: ({ key }) => {
                    if (key === 'copy') onCopy()
                    if (key === 'reset') onReset()
                  }
                }}
                trigger={['click']}
                placement="bottomRight"
              >
                <S.HeaderMenuButton type="button" aria-label="Opções da conversa" disabled={isResetting}>
                  <MoreOutlined />
                </S.HeaderMenuButton>
              </Dropdown>
              {headerActions}
              {!embedded && <Tooltip title="Minimizar">
                <S.HeaderButton type="button" aria-label="Minimizar conversa" onClick={minimize}>
                  <MinusOutlined />
                </S.HeaderButton>
              </Tooltip>}
            </S.HeaderActions>
          </S.ChatHeader>
          <S.MessagesArea ref={messagesAreaRef} aria-live="polite" aria-busy={isSending}>
            {isLoadingHistory && !messages.length && <S.HistorySkeleton>
              <Skeleton active title={false} paragraph={{ rows: 3 }} />
            </S.HistorySkeleton>}
            {!isLoadingHistory && !messages.length && <S.EmptyState>
              <strong>{emptyTitle}</strong><span>Sugestões</span>
              <S.SuggestionList>
                {suggestions.map(suggestion => <S.SuggestionButton
                  key={suggestion} type="button" disabled={isSending || isResetting}
                  onClick={() => { onSend(suggestion) }}
                >{suggestion}</S.SuggestionButton>)}
              </S.SuggestionList>
            </S.EmptyState>}
            {messages.map(chatMessage => <S.MessageRow key={chatMessage.id} $role={chatMessage.role}>
              <S.MessageBubble $role={chatMessage.role} $failed={chatMessage.state === 'failed'}>
                {chatMessage.state === 'streaming' && !chatMessage.content
                  ? <S.TypingIndicator aria-label="Gerando resposta"><span /><span /><span /></S.TypingIndicator>
                  : renderMessage(chatMessage)}
              </S.MessageBubble>
            </S.MessageRow>)}
            {embedded && messages.length > 0 && !isSending && suggestions.length > 0 && <S.SuggestionList>
              {suggestions.map(suggestion => <S.SuggestionButton
                key={suggestion} type="button" disabled={controlsDisabled}
                onClick={() => { onSend(suggestion) }}
              >{suggestion}</S.SuggestionButton>)}
            </S.SuggestionList>}
          </S.MessagesArea>
          <S.ChatComposer>
            {showComposerContext && <S.ComposerContext><FileTextOutlined aria-hidden="true" /><span>{title}</span></S.ComposerContext>}
            <S.ComposerInputRow $hasControls={Boolean(composerControls)}>
              <Input.TextArea
                ref={inputRef} value={draft} aria-label={placeholder}
                onChange={event => { onDraftChange(event.target.value) }}
                onKeyDown={inputKeyDown} placeholder={placeholder}
                autoSize={{ minRows: 1, maxRows: 4 }} maxLength={1000} disabled={controlsDisabled}
              />
              {composerControls}
              {isSending
                ? <Tooltip title="Interromper">
                  <Button danger icon={<StopOutlined />} onClick={onStop} aria-label="Interromper resposta" />
                </Tooltip>
                : <Button
                    type="primary" icon={<SendOutlined />} onClick={() => { onSend() }}
                    disabled={!draft.trim() || controlsDisabled} aria-label="Enviar pergunta"
                  />}
            </S.ComposerInputRow>
            {notice && <S.ComposerNotice>{notice}</S.ComposerNotice>}
          </S.ChatComposer>
        </S.ChatPanel>
        : <S.ChatLauncher ref={launcherRef} type="button" onClick={() => { setIsOpen(true) }} aria-label={launcherLabel}>
          <MessageOutlined /><span>Pergunte à Nora</span>
        </S.ChatLauncher>}
    </S.ChatRoot>
  )
  return embedded ? content : createPortal(content, document.body)
}
